import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, Tooltip, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
} from 'recharts';
import {
  Heart, Smile, ThumbsUp, Award, Gift, ClipboardCheck, AlertTriangle,
  Sparkles, Cake, Trophy, Loader2, Music, Coffee, Activity, Calendar,
  Info, Brain, BookOpen, Users as UsersIcon, Pizza, Flag, Plus, Trash2,
  Download,
} from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { RecognitionDialog } from '@/components/client/RecognitionDialog';
import WellbeingSurveysDialog from '@/components/client/WellbeingSurveysDialog';
import ProgramActivationHistoryDialog from '@/components/client/ProgramActivationHistoryDialog';
import BirthdayCalendarCard from '@/components/client/BirthdayCalendarCard';

// Founder UAT 2026-06 — vivid, executive-level chart palette (no line charts).
const VIVID = ['#3B82F6', '#F97316', '#EF4444', '#EAB308', '#10B981', '#EC4899', '#8B5CF6'];

// ---- Recommended programs (Sesión J-D) ---------------------------------
// Catalog 2026-06 (founder UAT remediation) — the full five-program catalog is
// requestable. One OPEN request per (organization, program); other programs
// stay requestable while one is under review. Extensible for HM Admin config.
const RECOMMENDED_PROGRAMS = [
  {
    code: 'wellbeing_integral',
    icon: Heart,
    iconBg: 'bg-pink-100 text-pink-600',
    badgeClass: 'bg-pink-100 text-pink-700 border-pink-200',
  },
  {
    code: 'celebrations_birthdays',
    icon: Cake,
    iconBg: 'bg-amber-100 text-amber-600',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    code: 'recognition_thanks',
    icon: Award,
    iconBg: 'bg-emerald-100 text-emerald-600',
    badgeClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  {
    code: 'mental_health_support',
    icon: Brain,
    iconBg: 'bg-violet-100 text-violet-600',
    badgeClass: 'bg-violet-100 text-violet-700 border-violet-200',
  },
  {
    code: 'learning_development',
    icon: BookOpen,
    iconBg: 'bg-teal-100 text-[#176A70]',
    badgeClass: 'bg-teal-100 text-[#176A70] border-teal-200',
  },
];

const initials = (name) => (name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

function KpiCard({ icon: Icon, label, value, hint, accent = 'primary', cta, testid, onClick, ctaTestid }) {
  // Founder UAT 2026-06 — vivid wellbeing palette (red/blue/yellow/orange…).
  const accents = {
    primary: 'from-blue-500/25 to-blue-500/10 text-blue-600',
    success: 'from-emerald-500/25 to-emerald-500/10 text-emerald-600',
    warning: 'from-amber-400/30 to-amber-400/10 text-amber-600',
    destructive: 'from-red-500/25 to-red-500/10 text-red-600',
    info: 'from-orange-500/25 to-orange-500/10 text-orange-600',
    pink: 'from-pink-500/25 to-pink-500/10 text-pink-600',
  };
  const bars = {
    primary: 'bg-blue-500', success: 'bg-emerald-500', warning: 'bg-amber-400',
    destructive: 'bg-red-500', info: 'bg-orange-500', pink: 'bg-pink-500',
  };
  return (
    <Card className="p-4 shadow-card hover:shadow-elevated transition-smooth relative overflow-hidden" data-testid={testid}>
      <span className={`absolute inset-x-0 top-0 h-1 ${bars[accent]}`} aria-hidden="true" />
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${accents[accent]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium leading-tight">{label}</p>
          <p className="text-xl font-bold tabular-nums mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
          {cta && (
            <button
              type="button"
              onClick={onClick}
              className="text-xs text-primary hover:underline mt-1 cursor-pointer"
              data-testid={ctaTestid}
            >
              {cta}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

// Sprint TH-C — All mocks removed. Every visible number, badge and list on
// this page is sourced from a real backend endpoint. Empty datasets render
// honest empty states instead of seeded pseudo-data.
//
// (The previous `seeded()` helper was deleted along with the at-risk and
// "personalized recognition" mocks. Birthdays show the real opt-in list;
// the radar shows the wellbeing aggregator output; alerts come from the
// new `/client/theart/alerts` endpoint; recognition suggestions come from
// `/client/theart/recognition-suggestions`.)

export default function ClientTalentHeart() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'es').startsWith('en') ? 'en' : 'es';
  const { user: authUser } = useApp();
  const canActivate = ['client_admin', 'client_hr'].includes(authUser?.role);
  const [overview, setOverview] = useState(null);
  const [collabs, setCollabs] = useState([]);
  const [realBirthdays, setRealBirthdays] = useState([]);
  const [birthdayMeta, setBirthdayMeta] = useState(null);
  const [interestsData, setInterestsData] = useState(null);
  const [recStats, setRecStats] = useState(null);
  const [recLeaderboard, setRecLeaderboard] = useState([]);
  const [recentRecognitions, setRecentRecognitions] = useState([]);
  const [recDialogOpen, setRecDialogOpen] = useState(false);
  const [surveysDialogOpen, setSurveysDialogOpen] = useState(false);
  const [parHistoryOpen, setParHistoryOpen] = useState(false);
  const [wellbeingKpis, setWellbeingKpis] = useState(null);
  const [activePrograms, setActivePrograms] = useState([]);
  const [alertsData, setAlertsData] = useState(null);              // TH-C
  const [recoSuggestions, setRecoSuggestions] = useState([]);      // TH-C
  const [loading, setLoading] = useState(true);
  const [activatingCode, setActivatingCode] = useState(null);
  const [activatedCodes, setActivatedCodes] = useState(() => new Set());
  const [parItems, setParItems] = useState([]);
  const [detail, setDetail] = useState(null);

  // TalentHeart 2026-06 — only ONE open request per program is allowed
  // (submitted / information required / approved-proposal-in-preparation).
  const OPEN_REQ_STATUSES = ['pending', 'info_required', 'approved'];
  const openReqByCode = useMemo(() => {
    const map = new Map();
    for (const it of parItems) {
      if (OPEN_REQ_STATUSES.includes(it.status) && !map.has(it.program_code)) {
        map.set(it.program_code, it);
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parItems]);

  const activateProgram = useCallback(async (code) => {
    setActivatingCode(code);
    try {
      const created = await clientApi.activateProgram({ program_code: code, suite_code: 'talentheart' });
      setActivatedCodes((prev) => new Set([...prev, code]));
      if (created) setParItems((prev) => [created, ...prev]);
      toast.success(t('clientTalentHeart.programsReco.activatedTitle'), {
        description: t('clientTalentHeart.programsReco.activatedBody'),
      });
    } catch (e) {
      const isDuplicate = e?.response?.status === 409
        && e?.response?.data?.detail === 'duplicate_open_request';
      toast.error(t('clientTalentHeart.programsReco.activateFailedTitle'), {
        description: isDuplicate
          ? t('clientTalentHeart.programsReco.duplicateOpenRequest')
          : formatApiError(e),
      });
    } finally {
      setActivatingCode(null);
    }
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, bd, ints, rs, rl, rr, wkpi, par, al, rsugg] = await Promise.all([
        clientApi.intelligenceOverview().catch(() => null),
        clientApi.listCollaborators().catch(() => []),
        clientApi.listTalentHeartBirthdays(2).catch(() => []),
        clientApi.listTalentHeartInterests().catch(() => null),
        clientApi.recognitionStats().catch(() => null),
        clientApi.recognitionLeaderboard('quarter', 5).catch(() => null),
        clientApi.listRecognitions({ limit: 6 }).catch(() => null),
        clientApi.wellbeingKpis().catch(() => null),
        clientApi.listProgramActivationRequests().catch(() => null),
        // Sprint TH-C — real at-risk + recognition suggestions.
        clientApi.wellbeingAlerts(8).catch(() => null),
        clientApi.recognitionSuggestions(60, 8).catch(() => null),
      ]);
      setOverview(o);
      setCollabs(Array.isArray(c) ? c : []);
      setRealBirthdays(Array.isArray(bd) ? bd : (bd?.rows || []));
      setBirthdayMeta(Array.isArray(bd) ? null : (bd?.meta || null));
      setInterestsData(ints);
      setRecStats(rs);
      setRecLeaderboard(Array.isArray(rl?.leaders) ? rl.leaders : []);
      setRecentRecognitions(Array.isArray(rr?.items) ? rr.items : []);
      setWellbeingKpis(wkpi);
      setAlertsData(al);                                          // TH-C
      setRecoSuggestions(Array.isArray(rsugg?.items) ? rsugg.items : []); // TH-C
      setParItems(Array.isArray(par?.items) ? par.items : []);
      // Session L-A — show each active program exactly once. The
      // program_activation_requests collection can hold multiple approved
      // rows per program (e.g. recurring activations); the dashboard card
      // is meant to surface "what's currently active", not a request log.
      // Dedup by program_code, keep the most recent approval per program.
      setActivePrograms(() => {
        if (!Array.isArray(par?.items)) return [];
        // Approved means ONLY "proposal in preparation" — a program is
        // active exclusively after a post-contract activation event.
        const activatedByCode = new Map();
        for (const it of par.items) {
          if (it.status !== 'activated') continue;
          const code = it.program_code;
          const existing = activatedByCode.get(code);
          if (!existing) {
            activatedByCode.set(code, it);
            continue;
          }
          // keep the most recent decided_at / created_at
          const existingTs = existing.decided_at || existing.updated_at || existing.created_at || '';
          const incomingTs = it.decided_at || it.updated_at || it.created_at || '';
          if (incomingTs > existingTs) activatedByCode.set(code, it);
        }
        return Array.from(activatedByCode.values());
      });
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    const happiness = wellbeingKpis?.happiness_pct ?? null;
    const engagement = wellbeingKpis?.engagement_pct ?? null;
    const participation = wellbeingKpis?.participation_pct ?? null;
    const recognitions = recStats?.total_quarter ?? 0;
    // Truthful states — never claim results without supporting responses.
    const surveyState = wellbeingKpis?.survey_state || 'none';
    const responsesExist = !!wellbeingKpis?.responses_exist;
    const atRisk = responsesExist
      ? (alertsData?.high_count || 0) + (alertsData?.mid_count || 0)
      : null;
    const thisMonth = new Date().getMonth() + 1;
    const birthdaysNeverCollected = birthdayMeta ? !!birthdayMeta.never_collected : realBirthdays.length === 0;
    const birthdays = birthdaysNeverCollected
      ? null
      : realBirthdays.filter((b) => b.month === thisMonth).length;
    return { happiness, engagement, recognitions, atRisk, birthdays, participation, surveyState, responsesExist, birthdaysNeverCollected };
  }, [realBirthdays, birthdayMeta, recStats, wellbeingKpis, alertsData]);

  // Sprint A6.2 — radar consumes real per-dimension % from wellbeing-kpis.
  // Picks 5 of the 7 dimensions to match existing UI (5 i18n labels d1..d5).
  const RADAR_DIM_KEYS = ['happiness', 'engagement', 'wellbeing', 'leader_relationship', 'belonging'];
  const radarData = useMemo(() => {
    const byKey = Object.fromEntries(
      (wellbeingKpis?.radar || []).map((d) => [d.dimension, d.pct]),
    );
    return RADAR_DIM_KEYS.map((key, i) => ({
      dim: t(`clientTalentHeart.radar.d${i + 1}`),
      value: byKey[key] ?? 0,
    }));
  }, [wellbeingKpis, t]);

  const radarHasData = useMemo(
    () => radarData.some((r) => r.value > 0),
    [radarData],
  );

  const radarAvg = useMemo(() =>
    Math.round(radarData.reduce((s, r) => s + r.value, 0) / Math.max(1, radarData.length)),
    [radarData],
  );

  // Sprint A6.1 — real leaderboard from recognitions collection (top 5 this quarter).
  const recognized = useMemo(() =>
    recLeaderboard.map((l) => ({
      id: l.user_id,
      name: l.name,
      count: l.count,
    })),
    [recLeaderboard],
  );

  // Granular birthday-source states — never a generic "no data".
  const birthdayEmptyKey = useMemo(() => {
    const m = birthdayMeta;
    if (!m) return 'neverCollected';
    if (m.never_collected) return 'neverCollected';
    if ((m.authorized || 0) === 0 && (m.declined || 0) > 0) return 'allDeclined';
    if ((m.authorized || 0) === 0) return 'incomplete';
    return 'nonePeriod';
  }, [birthdayMeta]);

  const birthdays = useMemo(() => {
    // Sprint TH-C — real voluntary birthdays only. Empty list renders an
    // honest empty state in the upcoming-birthdays panel (no more mocks).
    return realBirthdays.map((b) => ({
      id: b.user_id,
      name: b.name,
      position: b.position,
      date: b.date,
    }));
  }, [realBirthdays]);

  // Sprint Q1.1 — real interests from voluntary personal_profiles.
  // Each dimension shows up to top 3 values with %. No respondents → empty.
  const INTEREST_DIM_ORDER = ['sport', 'team', 'dessert', 'food', 'hobbies'];
  const INTEREST_DIM_ICONS = {
    sport: Activity, team: Flag, dessert: Coffee, food: Pizza, hobbies: Sparkles,
  };
  const interestsList = useMemo(() => {
    const dims = interestsData?.dimensions || {};
    return INTEREST_DIM_ORDER.map((key) => {
      const d = dims[key] || { respondents: 0, top: [] };
      return {
        key,
        icon: INTEREST_DIM_ICONS[key] || Sparkles,
        respondents: d.respondents || 0,
        insufficientSample: !!d.insufficient_sample,
        top: Array.isArray(d.top) ? d.top : [],
      };
    });
  }, [interestsData]);
  const interestsHasAny = interestsList.some((d) => d.top.length > 0 || d.insufficientSample);
  const interestsTotalUsers = interestsData?.total_users || 0;

  // Sprint A6.1 — real recent recognitions from collection (last 6).
  const recentRec = useMemo(() => {
    return recentRecognitions.slice(0, 6).map((r) => {
      const created = r.created_at ? new Date(r.created_at) : null;
      let ago = '—';
      if (created) {
        const diffMin = Math.floor((Date.now() - created.getTime()) / 60000);
        if (diffMin < 60) ago = `${Math.max(1, diffMin)}m`;
        else if (diffMin < 24 * 60) ago = `${Math.floor(diffMin / 60)}h`;
        else ago = `${Math.floor(diffMin / (60 * 24))}d`;
      }
      return {
        id: r.id,
        from: r.from_user_name || '—',
        to: r.to_user_name || '—',
        type: r.type,
        category: r.category,
        excerpt: r.message || '',
        ago,
      };
    });
  }, [recentRecognitions]);

  const alerts = useMemo(() => {
    // Sprint TH-C — real at-risk users from /client/theart/alerts.
    // Maps backend rows to the existing UI shape (name/position/risk).
    return (alertsData?.items || []).slice(0, 4).map((a) => ({
      name: a.name,
      position: a.position,
      risk: a.risk,
    }));
  }, [alertsData]);

  const suggestions = [
    { key: 'personalReco',     icon: ThumbsUp, count: kpis.recognitions,  detailKind: 'recognition' },
    { key: 'birthdayDetail',   icon: Cake,     count: kpis.birthdays,     detailKind: 'calendar' },
    { key: 'wellbeingSurvey',  icon: ClipboardCheck, count: kpis.participation, detailKind: 'survey' },
  ];

  // Sprint TH-C — real "users not recognised in 60+ days" from the backend.
  // Replaces the previous derivation from the top-5 leaderboard.
  const unrecognized60d = useMemo(
    () => recoSuggestions.slice(0, 6).map((r) => ({
      id: r.user_id,
      name: r.name,
      position: r.position,
    })),
    [recoSuggestions],
  );

  // Founder UAT 2026-06 — recognition distribution charts from real stats.
  const categoryChart = useMemo(() => {
    const byCat = recStats?.by_category || {};
    return Object.entries(byCat)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({ key: k, name: t(`recognitionDialog.categories.${k}`, { defaultValue: k }), value: v }));
  }, [recStats, t]);
  const typeChart = useMemo(() => {
    const byType = recStats?.by_type || {};
    return [
      { key: 'peer', name: t('recognitionDialog.typePeer'), value: byType.peer || 0 },
      { key: 'manager', name: t('recognitionDialog.typeManager'), value: byType.manager || 0 },
      { key: 'company', name: t('recognitionDialog.typeCompany'), value: byType.company || 0 },
    ];
  }, [recStats, t]);

  if (loading) {
    return (
      <>
        <Topbar title={t('clientTalentHeart.title')} subtitle={t('clientTalentHeart.subtitle')} />
        <div className="py-16 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('clientTalentHeart.title')}
            <Badge className="bg-primary/15 text-primary border-primary/30">
              {t('suiteChip.talentheart')}
            </Badge>
          </span>
        )}
        subtitle={t('clientTalentHeart.subtitle')}
        actions={(
          <a
            href={clientApi.talentHeartReportPdfUrl(locale)}
            className="inline-flex items-center"
            data-testid="theart-export-pdf-btn"
          >
            <Button size="sm" variant="outline" className="gap-1.5">
              <Download className="h-4 w-4" />
              {t('clientTalentHeart.exportPdf')}
            </Button>
          </a>
        )}
      />

      <div className="p-6 lg:p-8 space-y-5" data-testid="client-theart-page">
        {/* Info panel */}
        <Card className="p-5 shadow-card flex items-start gap-3 bg-gradient-to-br from-pink-500/5 to-transparent">
          <Info className="h-5 w-5 text-pink-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-sm">{t('clientTalentHeart.what.title')}</h3>
            <p className="text-sm text-muted-foreground mt-1">{t('clientTalentHeart.what.body')}</p>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <KpiCard icon={Smile} label={t('clientTalentHeart.kpi.happiness')}
                   value={kpis.happiness === null ? '—' : `${kpis.happiness}%`}
                   accent="success" testid="theart-kpi-happiness" />
          <KpiCard icon={Heart} label={t('clientTalentHeart.kpi.engagement')}
                   value={kpis.engagement === null ? '—' : `${kpis.engagement}%`}
                   accent="pink" testid="theart-kpi-engagement" />
          <KpiCard icon={Trophy} label={t('clientTalentHeart.kpi.recognitions')}
                   value={kpis.recognitions} accent="warning" testid="theart-kpi-recognitions" />
          <KpiCard icon={AlertTriangle} label={t('clientTalentHeart.kpi.atRisk')}
                   value={kpis.atRisk === null ? '—' : kpis.atRisk}
                   hint={kpis.atRisk === null
                     ? t('clientTalentHeart.kpi.atRiskNoData')
                     : t('clientTalentHeart.kpi.atRiskHint')}
                   accent="destructive" cta={t('clientTalentHeart.kpi.viewAlerts')}
                   onClick={() => setDetail({ kind: 'alerts' })}
                   ctaTestid="theart-view-alerts"
                   testid="theart-kpi-atrisk" />
          <KpiCard icon={Cake} label={t('clientTalentHeart.kpi.birthdays')}
                   value={kpis.birthdays === null ? '—' : kpis.birthdays}
                   hint={(kpis.birthdays === null || kpis.birthdays === 0)
                     ? t(`clientTalentHeart.birthdayStates.${birthdayEmptyKey}`)
                     : undefined}
                   accent="info"
                   cta={t('clientTalentHeart.kpi.viewCalendar')}
                   onClick={() => setDetail({ kind: 'calendar' })}
                   ctaTestid="theart-view-calendar"
                   testid="theart-kpi-birthdays" />
          <KpiCard icon={ClipboardCheck} label={t('clientTalentHeart.kpi.survey')}
                   value={t(`clientTalentHeart.kpi.surveyState.${kpis.surveyState}`, {
                     defaultValue: t('clientTalentHeart.kpi.surveyNoData'),
                   })}
                   hint={kpis.surveyState === 'none'
                     ? t('clientTalentHeart.kpi.surveyNoneHint')
                     : !kpis.responsesExist
                       ? t('clientTalentHeart.kpi.surveyNoResponsesYet')
                       : kpis.participation === null
                         ? t('clientTalentHeart.kpi.surveyNoResponses')
                         : t('clientTalentHeart.kpi.participation', { pct: kpis.participation })}
                   accent="primary"
                   cta={kpis.surveyState === 'none'
                     ? `+ ${t('clientTalentHeart.createSurveyCta', { defaultValue: 'Crear encuesta de bienestar' })}`
                     : t('clientTalentHeart.kpi.viewResults')}
                   onClick={() => setSurveysDialogOpen(true)}
                   ctaTestid="theart-view-results"
                   testid="theart-kpi-survey" />
        </div>

        {/* Row 1: Radar + Recognized + Birthdays */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-5 shadow-card" data-testid="theart-radar">
            <div className="mb-3">
              <h2 className="font-semibold">{t('clientTalentHeart.radar.title')}</h2>
              <p className="text-xs text-muted-foreground">{t('clientTalentHeart.radar.subtitle')}</p>
            </div>
            {!kpis.responsesExist ? (
              <div className="h-56 flex flex-col items-center justify-center px-4 gap-3" data-testid="theart-radar-nodata">
                <p className="text-sm text-muted-foreground text-center">
                  {kpis.surveyState === 'none'
                    ? t('clientTalentHeart.radar.noSurvey')
                    : t('clientTalentHeart.radar.noResponses')}
                </p>
                {canActivate && kpis.surveyState === 'none' && (
                  <Button
                    size="sm"
                    className="gap-1.5 bg-gradient-to-r from-pink-500 to-orange-500 hover:opacity-90 text-white"
                    onClick={() => setSurveysDialogOpen(true)}
                    data-testid="theart-create-survey-cta"
                  >
                    <Plus className="h-4 w-4" />
                    {t('clientTalentHeart.createSurveyCta', { defaultValue: 'Crear encuesta de bienestar' })}
                  </Button>
                )}
              </div>
            ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="dim" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                  <Radar name="score" dataKey="value" stroke="#ec4899" fill="#ec4899" fillOpacity={0.4} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            )}
            {kpis.responsesExist && (wellbeingKpis?.insufficient_sample ? (
              <p className="text-center text-xs text-muted-foreground mt-2 px-2" data-testid="theart-radar-insufficient">
                {t('clientTalentHeart.radar.insufficientSample')}
              </p>
            ) : (
              <p className="text-center text-sm font-semibold mt-2">
                {t('clientTalentHeart.radar.avg', { pct: radarAvg })}
              </p>
            ))}
          </Card>

          <Card className="p-5 shadow-card" data-testid="theart-recognized">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">{t('clientTalentHeart.recognized.title')}</h2>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => setRecDialogOpen(true)}
                data-testid="theart-give-recognition"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('clientTalentHeart.recognized.giveCta')}
              </Button>
            </div>
            {recognized.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4" data-testid="theart-recognized-empty">
                {t('clientTalentHeart.recognized.empty')}
              </p>
            ) : (
              <ul className="space-y-2">
                {recognized.map((u, i) => (
                  <li key={u.id || i} className="flex items-center gap-2" data-testid={`theart-recognized-${i}`}>
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-warning/15 text-warning text-xs font-semibold">
                      {initials(u.name)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                    </div>
                    <Badge className="bg-warning/15 text-warning border-warning/30 text-xs">
                      <Trophy className="h-3 w-3 mr-1" />{u.count}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <BirthdayCalendarCard
            emptyStateText={t(`clientTalentHeart.birthdayStates.${birthdayEmptyKey}`)}
          />
        </div>

        {/* Founder UAT 2026-06 — Recognition analytics (donut + bars, real data only) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="theart-analytics-row">
          <Card className="p-5 shadow-card relative overflow-hidden" data-testid="theart-chart-categories">
            <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-amber-400 to-red-500" aria-hidden="true" />
            <h2 className="font-semibold mb-1">{t('clientTalentHeart.analytics.byCategoryTitle', { defaultValue: 'Reconocimientos por categoría' })}</h2>
            <p className="text-xs text-muted-foreground mb-2">{t('clientTalentHeart.analytics.byCategorySubtitle', { defaultValue: 'Distribución real de todos los reconocimientos' })}</p>
            {categoryChart.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="theart-chart-categories-empty">
                {t('clientTalentHeart.analytics.empty', { defaultValue: 'Aún no hay reconocimientos registrados. Otorga el primero con "Dar reconocimiento".' })}
              </p>
            ) : (
              <div className="h-56 flex items-center">
                <ResponsiveContainer width="55%" height="100%">
                  <PieChart>
                    <Pie data={categoryChart} dataKey="value" nameKey="name"
                         innerRadius={45} outerRadius={80} paddingAngle={3} stroke="none">
                      {categoryChart.map((entry, i) => (
                        <Cell key={entry.key} fill={VIVID[i % VIVID.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="flex-1 space-y-1.5 pr-2">
                  {categoryChart.map((c, i) => (
                    <li key={c.key} className="flex items-center gap-2 text-xs" data-testid={`theart-cat-legend-${c.key}`}>
                      <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: VIVID[i % VIVID.length] }} />
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="font-semibold tabular-nums">{c.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <Card className="p-5 shadow-card relative overflow-hidden" data-testid="theart-chart-types">
            <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 via-emerald-400 to-blue-500" aria-hidden="true" />
            <h2 className="font-semibold mb-1">{t('clientTalentHeart.analytics.byTypeTitle', { defaultValue: 'Reconocimientos por tipo' })}</h2>
            <p className="text-xs text-muted-foreground mb-2">{t('clientTalentHeart.analytics.byTypeSubtitle', { defaultValue: 'Entre pares, de liderazgo y de la empresa' })}</p>
            {typeChart.every((r) => r.value === 0) ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="theart-chart-types-empty">
                {t('clientTalentHeart.analytics.empty', { defaultValue: 'Aún no hay reconocimientos registrados. Otorga el primero con "Dar reconocimiento".' })}
              </p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={typeChart} layout="vertical" margin={{ left: 10, right: 24, top: 8, bottom: 0 }}>
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={26}>
                      {typeChart.map((entry, i) => (
                        <Cell key={entry.key} fill={['#3B82F6', '#F97316', '#EAB308'][i % 3]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* Row 2: Interests + Recent recognitions + Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-5 shadow-card" data-testid="theart-interests">
            <h2 className="font-semibold mb-3">{t('clientTalentHeart.interests.title')}</h2>
            {!interestsHasAny ? (
              <p className="text-sm text-muted-foreground py-3" data-testid="theart-interests-empty">
                {t('clientTalentHeart.interests.empty')}
              </p>
            ) : (
              <ul className="space-y-3">
                {interestsList.map((it) => {
                  const Icon = it.icon;
                  const headline = it.top[0];
                  const rest = it.top.slice(1);
                  return (
                    <li key={it.key} className="flex items-start gap-3" data-testid={`theart-interest-${it.key}`}>
                      <Icon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-xs text-muted-foreground truncate">
                            {t(`clientTalentHeart.interests.${it.key}`)}
                          </p>
                          <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                            {t('clientTalentHeart.interests.respondentsSuffix', { n: it.respondents, total: interestsTotalUsers })}
                          </p>
                        </div>
                        {headline ? (
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <p className="text-sm font-medium truncate" data-testid={`theart-interest-${it.key}-top1`}>
                              {headline.value}
                            </p>
                            <span className="text-xs tabular-nums shrink-0">{headline.pct}%</span>
                          </div>
                        ) : it.insufficientSample ? (
                          <p className="text-sm text-muted-foreground italic mt-0.5" data-testid={`theart-interest-${it.key}-insufficient`}>
                            {t('clientTalentHeart.interests.insufficientSample')}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground italic mt-0.5">
                            {t('clientTalentHeart.interests.noResponses')}
                          </p>
                        )}
                        {rest.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`theart-interest-${it.key}-runners`}>
                            {rest.map((r, idx) => (
                              <Badge
                                key={`${r.value}-${idx}`}
                                variant="outline"
                                className="text-xs font-normal"
                                data-testid={`theart-interest-${it.key}-top${idx + 2}`}
                              >
                                {r.value} · {r.pct}%
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-5 shadow-card" data-testid="theart-recent-rec">
            <h2 className="font-semibold mb-3">{t('clientTalentHeart.recent.title')}</h2>
            {recentRec.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3">{t('clientTalentHeart.recent.empty')}</p>
            ) : (
              <ul className="space-y-3">
                {recentRec.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`theart-rec-${i}`}>
                    <Heart className="h-4 w-4 text-pink-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs">
                        <span className="font-medium">{r.from}</span>
                        <span className="text-muted-foreground"> → </span>
                        <span className="font-medium">{r.to}</span>
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{r.excerpt}</p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{r.ago}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5 shadow-card" data-testid="theart-alerts">
            <h2 className="font-semibold mb-3">{t('clientTalentHeart.alerts.title')}</h2>
            {alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3" data-testid="theart-alerts-empty">
                {kpis.responsesExist
                  ? t('clientTalentHeart.alerts.empty')
                  : t('clientTalentHeart.alerts.noResponses')}
              </p>
            ) : (
              <ul className="space-y-2">
                {alerts.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm" data-testid={`theart-alert-${i}`}>
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-destructive/15 text-destructive text-xs font-semibold">
                      {initials(a.name)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{a.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{a.position}</p>
                    </div>
                    <Badge className={
                      a.risk === 'high'
                        ? 'bg-destructive/15 text-destructive border-destructive/30 text-xs'
                        : 'bg-warning/15 text-warning border-warning/30 text-xs'
                    }>
                      {a.risk === 'high'
                        ? t('clientTalentHeart.alerts.highRisk')
                        : t('clientTalentHeart.alerts.midRisk')}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Suggestions + Programs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-5 shadow-card lg:col-span-2" data-testid="theart-suggestions">
            <h2 className="font-semibold mb-3">{t('clientTalentHeart.suggestions.title')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {suggestions.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.key} className="border rounded-lg p-3 flex items-center gap-3 hover:shadow-md transition-smooth"
                       data-testid={`theart-sugg-${s.key}`}>
                    <Icon className="h-5 w-5 text-pink-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t(`clientTalentHeart.suggestions.${s.key}`)}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">{s.count}</p>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setDetail({ kind: s.detailKind })}
                      data-testid={`theart-sugg-${s.key}-open`}
                    >
                      {t('clientTalentHeart.suggestions.cta')}
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5 shadow-card" data-testid="theart-programs">
            <h2 className="font-semibold mb-3">{t('clientTalentHeart.programs.title')}</h2>
            {activePrograms.length === 0 ? (
              <p className="text-sm text-muted-foreground py-3" data-testid="theart-programs-empty">
                {t('clientTalentHeart.programs.empty')}
              </p>
            ) : (
              <ul className="space-y-2">
                {activePrograms.map((p, i) => (
                  <li key={p.id} className="flex items-center justify-between text-sm gap-2"
                      data-testid={`theart-program-${i}`}>
                    <span className="font-medium truncate">
                      {t(`clientTalentHeart.programsReco.items.${p.program_code}.name`, {
                        defaultValue: p.program_code,
                      })}
                    </span>
                    <Badge className="bg-success/15 text-success border-success/30 text-xs shrink-0">
                      {t('clientTalentHeart.programs.status')}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Programas recomendados (Sesión J-D) */}
        <Card className="p-5 shadow-card" data-testid="theart-programs-reco">
          <div className="mb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">{t('clientTalentHeart.programsReco.title')}</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                {t('clientTalentHeart.programsReco.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParHistoryOpen(true)}
                data-testid="theart-view-requests-btn"
                className="gap-1.5"
              >
                <ClipboardCheck className="h-4 w-4" />
                {t('clientTalentHeart.viewRequests')}
              </Button>
              <Badge className="bg-pink-50 text-pink-700 border-pink-200 inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {t('clientTalentHeart.programsReco.badge')}
              </Badge>
            </div>
          </div>
          <ul className="space-y-3">
            {RECOMMENDED_PROGRAMS.map((p) => {
              const Icon = p.icon;
              const openReq = openReqByCode.get(p.code);
              const isActivated = activatedCodes.has(p.code);
              const isLoading = activatingCode === p.code;
              const openLabel = openReq
                ? t(`clientTalentHeart.programsReco.openStatus.${openReq.status}`, {
                    defaultValue: t('clientTalentHeart.programsReco.openStatus.pending'),
                  })
                : null;
              return (
                <li
                  key={p.code}
                  className="flex items-start gap-4 border rounded-lg p-4 hover:shadow-md transition-smooth"
                  data-testid={`theart-reco-${p.code}`}
                >
                  <div className={`inline-flex h-12 w-12 items-center justify-center rounded-lg shrink-0 ${p.iconBg}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <h3 className="font-semibold text-base">
                        {t(`clientTalentHeart.programsReco.items.${p.code}.name`)}
                      </h3>
                      <Badge variant="outline" className={`${p.badgeClass} text-xs w-fit`}>
                        {t(`clientTalentHeart.programsReco.items.${p.code}.badge`)}
                      </Badge>
                      {openReq && (
                        <Badge
                          variant="outline"
                          className={`text-xs w-fit ${openReq.status === 'approved'
                            ? 'bg-success/15 text-success border-success/30'
                            : 'bg-warning/15 text-warning border-warning/30'}`}
                          data-testid={`theart-reco-open-status-${p.code}`}
                        >
                          {openLabel}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t(`clientTalentHeart.programsReco.items.${p.code}.desc`)}
                    </p>
                  </div>
                  {canActivate && !openReq && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      disabled={isLoading || isActivated}
                      onClick={() => activateProgram(p.code)}
                      data-testid={`theart-reco-activate-${p.code}`}
                    >
                      {isLoading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : isActivated
                          ? t('clientTalentHeart.programsReco.activatedTitle')
                          : t('clientTalentHeart.programsReco.activate')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {/* Founder UAT Wave 2 — post-activation explanation panel */}
          {activatedCodes.size > 0 && (
            <div className="mt-4 rounded-lg border border-success/30 bg-success/5 p-4" data-testid="theart-post-activation-panel">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-success" />
                {t('clientTalentHeart.postActivation.title', { defaultValue: 'What happens next?' })}
              </h3>
              <ol className="mt-2 space-y-1.5 text-xs text-muted-foreground list-decimal list-inside">
                <li>{t('clientTalentHeart.postActivation.step1', { defaultValue: 'Your CTH advisor is notified of the activation request.' })}</li>
                <li>{t('clientTalentHeart.postActivation.step2', { defaultValue: 'They review it and configure the program for your organisation.' })}</li>
                <li>{t('clientTalentHeart.postActivation.step3', { defaultValue: 'You can track the status at any time in the request history.' })}</li>
              </ol>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 gap-1.5"
                onClick={() => setParHistoryOpen(true)}
                data-testid="theart-post-activation-view-requests"
              >
                <ClipboardCheck className="h-3.5 w-3.5" />
                {t('clientTalentHeart.postActivation.cta', { defaultValue: 'View my requests' })}
              </Button>
            </div>
          )}
        </Card>

        <p className="text-xs text-muted-foreground italic">{t('clientTalentHeart.footerNote')}</p>
      </div>

      {/* Sprint TH-C — Inline detail dialog for Ver alertas / Ver calendario /
          Ver resultados / Personalized recognition. Dead 'sport' branch
          removed. All four panels now consume real backend data. */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid={`theart-detail-${detail?.kind || 'none'}`}>
          <DialogHeader>
            <DialogTitle>
              {detail?.kind === 'alerts'      && t('clientTalentHeart.detail.alertsTitle')}
              {detail?.kind === 'calendar'    && t('clientTalentHeart.detail.calendarTitle')}
              {detail?.kind === 'survey'      && t('clientTalentHeart.detail.surveyTitle')}
              {detail?.kind === 'recognition' && t('clientTalentHeart.detail.recognitionTitle')}
            </DialogTitle>
            <DialogDescription>
              {detail?.kind === 'alerts'      && t('clientTalentHeart.detail.alertsSubtitle')}
              {detail?.kind === 'calendar'    && t('clientTalentHeart.detail.calendarSubtitle')}
              {detail?.kind === 'survey'      && t('clientTalentHeart.detail.surveySubtitle')}
              {detail?.kind === 'recognition' && t('clientTalentHeart.detail.recognitionSubtitle')}
            </DialogDescription>
          </DialogHeader>

          {detail?.kind === 'alerts' && (
            <ul className="space-y-2 max-h-96 overflow-auto" data-testid="theart-detail-alerts-list">
              {alerts.length === 0
                ? <li className="text-sm text-muted-foreground py-4 text-center">{t('clientTalentHeart.detail.empty')}</li>
                : alerts.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-lg border" data-testid={`theart-alert-${i}`}>
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{a.name}</p>
                      <p className="text-xs text-muted-foreground">{a.position || '—'}</p>
                      <p className="text-xs mt-1">{t('clientTalentHeart.detail.riskReason')}</p>
                    </div>
                    <Badge className="bg-destructive/15 text-destructive border-destructive/30 text-xs">
                      {t('clientTalentHeart.detail.highRisk')}
                    </Badge>
                  </li>
                ))}
            </ul>
          )}

          {detail?.kind === 'calendar' && (
            <div className="space-y-2">
              {birthdayMeta && (
                <p className="text-xs text-muted-foreground" data-testid="theart-calendar-meta">
                  {t('clientTalentHeart.birthdayStates.metaSummary', {
                    eligible: birthdayMeta.eligible_total ?? 0,
                    recorded: birthdayMeta.birth_dates_recorded ?? 0,
                    authorized: birthdayMeta.authorized ?? 0,
                    declined: birthdayMeta.declined ?? 0,
                  })}
                </p>
              )}
              <ul className="grid grid-cols-1 gap-2 max-h-96 overflow-auto" data-testid="theart-detail-calendar-list">
                {birthdays.length === 0
                  ? (
                    <li className="text-sm text-muted-foreground py-4 text-center" data-testid="theart-calendar-empty-state">
                      {t(`clientTalentHeart.birthdayStates.${birthdayEmptyKey}`)}
                    </li>
                  )
                  : birthdays.map((b, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`theart-birthday-${i}`}>
                      <Cake className="h-4 w-4 text-info shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{b.name}</p>
                        <p className="text-xs text-muted-foreground">{b.position || '—'}</p>
                      </div>
                      <span className="text-xs font-semibold tabular-nums">{b.date}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {detail?.kind === 'survey' && (
            <div className="space-y-4" data-testid="theart-detail-survey">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('clientTalentHeart.detail.participation')}</span>
                  <span className="font-semibold tabular-nums">{kpis.participation}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${kpis.participation}%` }} />
                </div>
              </div>
              <div className="space-y-2">
                {radarData.map((d) => (
                  <div key={d.dim}>
                    <div className="flex justify-between text-xs mb-1">
                      <span>{d.dim}</span>
                      <span className="font-semibold tabular-nums">{d.value}%</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-success rounded-full" style={{ width: `${d.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail?.kind === 'recognition' && (
            <div className="space-y-3" data-testid="theart-detail-recognition">
              <p className="text-xs text-muted-foreground">
                {t('clientTalentHeart.detail.recognitionHint')}
              </p>
              {unrecognized60d.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {t('clientTalentHeart.detail.empty')}
                </p>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-auto" data-testid="theart-detail-recognition-list">
                  {unrecognized60d.map((u, i) => (
                    <li key={u.id || i}
                        className="flex items-center gap-3 p-3 rounded-lg border"
                        data-testid={`theart-reco-row-${i}`}>
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-warning/15 text-warning text-xs font-semibold shrink-0">
                        {initials(u.name)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.position || '—'}</p>
                      </div>
                      <Badge className="bg-warning/15 text-warning border-warning/30 text-xs">
                        {t('clientTalentHeart.detail.recognitionBadge')}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

        </DialogContent>
      </Dialog>

      <RecognitionDialog
        open={recDialogOpen}
        onClose={() => setRecDialogOpen(false)}
        currentUser={authUser}
        onCreated={load}
      />

      <WellbeingSurveysDialog
        open={surveysDialogOpen}
        onClose={() => setSurveysDialogOpen(false)}
        onChanged={load}
      />

      <ProgramActivationHistoryDialog
        open={parHistoryOpen}
        onOpenChange={setParHistoryOpen}
      />
    </>
  );
}
